'use client'

/**
 * The notification bell, on the account card (§102).
 *
 * ── Where it lives, and why there ───────────────────────────────────────────
 *
 * Inside the account cluster, immediately left of the avatar chip, sharing its
 * pill. Not floating loose in the header: "who am I" and "what happened to me"
 * are the same question asked twice, and putting them in one object is what
 * stops the header becoming a row of unrelated circles.
 *
 * ── What it costs when nothing is happening ─────────────────────────────────
 *
 * A count, once a minute, and nothing else. The LIST is fetched when the panel
 * opens and not before — twenty rendered notifications riding along with every
 * badge poll would make this the most expensive thing on a page that is mostly
 * cached HTML. The badge is seeded server-side by whoever renders the header, so
 * the first paint already has the right number and there is no flash of zero.
 *
 * Polling and not a socket: this is a courtesy feature on a product with one
 * server, and a persistent connection per open tab is an operational cost that
 * buys a notification arriving forty seconds sooner.
 *
 * ── The three dismissal handlers ────────────────────────────────────────────
 *
 * Escape, pointer-down outside, and focus leaving — the same three `AccountMenu`
 * carries, for the same reason: they are the ones always forgotten, and a panel
 * that traps you until you click precisely the button again is the difference
 * between a menu and a bug.
 */
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useCallback, useEffect, useId, useRef, useState, useTransition } from 'react'
import { relativeTime } from '@/lib/time'
import { badgeCount, detailsOf, iconFor, type NotificationRecord } from '@/lib/notifications'
import {
  clearNotifications,
  fetchNotifications,
  fetchUnreadCount,
  markNotificationsRead,
  removeNotification,
} from './notificationActions'

/** Once a minute. Often enough to feel live, rare enough to be free. */
const POLL_MS = 60_000

/** What fits in a dropdown before it stops being a dropdown. */
const PANEL_LIMIT = 12

export function NotificationBell({
  initialUnread = 0,
  /** `admin` renders inside the console's topbar chip, which is sized larger. */
  context = 'site',
}: {
  initialUnread?: number
  context?: 'site' | 'admin'
}) {
  const [open, setOpen] = useState(false)
  const [unread, setUnread] = useState(initialUnread)
  const [items, setItems] = useState<NotificationRecord[] | null>(null)
  const [pending, start] = useTransition()
  const wrapRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelId = useId()
  const router = useRouter()

  /*
   * The server is the authority on the badge, always.
   *
   * Every action below re-reads the page rather than adjusting a local counter,
   * because the same account is very often open in two tabs: decrementing
   * locally would leave the other tab confidently wrong until its next poll.
   */
  const load = useCallback(async () => {
    const page = await fetchNotifications(PANEL_LIMIT)
    setItems(page.notifications)
    setUnread(page.unread)
  }, [])

  useEffect(() => {
    if (!open) return

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      setOpen(false)
      buttonRef.current?.focus()
    }
    const onPointerDown = (e: PointerEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onFocusIn = (e: FocusEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }

    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('focusin', onFocusIn)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('focusin', onFocusIn)
    }
  }, [open])

  /*
   * The badge poll.
   *
   * Paused while the panel is open — what is on screen is fresher than anything
   * a poll would bring, and a count changing under an open list is the kind of
   * detail that makes a reader lose their place. `document.hidden` skips the
   * work entirely for a background tab, which is where most tabs are.
   */
  useEffect(() => {
    if (open) return
    const tick = async () => {
      if (document.hidden) return
      setUnread(await fetchUnreadCount())
    }
    const id = setInterval(tick, POLL_MS)
    return () => clearInterval(id)
  }, [open])

  function toggle() {
    const next = !open
    setOpen(next)
    if (next) void load()
  }

  /** Opening a notification is reading it — no separate gesture required. */
  function openItem(item: NotificationRecord) {
    setOpen(false)
    start(async () => {
      if (!item.readAt) {
        await markNotificationsRead([item.id])
        setUnread((n) => Math.max(0, n - 1))
      }
      if (item.href) router.push(item.href)
      else router.refresh()
    })
  }

  const empty = items !== null && items.length === 0

  return (
    <div className={`notif notif-${context}`} ref={wrapRef}>
      <button
        ref={buttonRef}
        type="button"
        className="notif-trigger"
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={open ? panelId : undefined}
        aria-label={
          unread > 0
            ? `Notifications — ${unread} unread`
            : 'Notifications'
        }
        title="Notifications"
      >
        <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
          <path
            d="M10 3.2a4.4 4.4 0 014.4 4.4c0 3.5 1.1 4.6 1.1 4.6H4.5s1.1-1.1 1.1-4.6A4.4 4.4 0 0110 3.2zM8.4 15a1.7 1.7 0 003.2 0"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {/* `aria-hidden`: the count is already in the button's accessible name
            above, and announcing it twice is how a screen reader ends up saying
            "notifications 3 unread 3". */}
        {unread > 0 && (
          <span className="notif-badge" aria-hidden="true">
            {badgeCount(unread)}
          </span>
        )}
      </button>

      {open && (
        <div className="notif-panel" id={panelId} role="menu" aria-label="Notifications">
          <header className="notif-panel-head">
            <h2>Notifications</h2>
            <span className="notif-panel-count">
              {unread > 0 ? `${unread} unread` : 'all caught up'}
            </span>
          </header>

          <div className="notif-tools">
            <button
              type="button"
              className="notif-tool"
              disabled={pending || unread === 0}
              onClick={() =>
                start(async () => {
                  await markNotificationsRead()
                  await load()
                  router.refresh()
                })
              }
            >
              Mark all read
            </button>
            {/* Clears only what has been READ. A one-word button in a corner
                must not be able to destroy something you have not looked at;
                the notifications page has the version that can, behind a
                confirmation and a sentence. */}
            <button
              type="button"
              className="notif-tool"
              disabled={pending || items === null || items.length === 0}
              onClick={() =>
                start(async () => {
                  await clearNotifications(true)
                  await load()
                  router.refresh()
                })
              }
            >
              Clear read
            </button>
          </div>

          <div className="notif-list">
            {items === null ? (
              /* Three skeleton rows, at the height real rows will be. A spinner
                 would be honest about waiting and dishonest about size: the
                 panel would snap from 60px to 400px when the data lands. */
              <ul className="notif-skeleton" aria-hidden="true">
                {[0, 1, 2].map((i) => (
                  <li key={i} />
                ))}
              </ul>
            ) : empty ? (
              <p className="notif-empty">
                Nothing yet. Anything that happens to your account shows up here.
              </p>
            ) : (
              <ul>
                {items.map((item) => (
                  <NotificationRow
                    key={item.id}
                    item={item}
                    onOpen={() => openItem(item)}
                    onDismiss={() =>
                      start(async () => {
                        await removeNotification(item.id)
                        await load()
                      })
                    }
                  />
                ))}
              </ul>
            )}
          </div>

          <footer className="notif-panel-foot">
            <Link href="/dashboard/notifications" onClick={() => setOpen(false)}>
              See everything →
            </Link>
          </footer>
        </div>
      )}
    </div>
  )
}

/**
 * How many figures fit in a dropdown row.
 *
 * Three, where the page shows all six. The panel is 26rem of scroll holding
 * twelve notifications, and a row that grows to four lines has taken the row
 * below it off the screen — at which point the dropdown has become the page it
 * links to, badly. The first three are the emitter's most important three,
 * because the emitter wrote them in order; "See everything" has the rest.
 */
const PANEL_DETAILS = 3

/**
 * One row.
 *
 * A `<button>` rather than an `<a>` even when the notification has an `href`:
 * pressing it does two things — mark it read, then navigate — and only one of
 * those is a link. The dismiss control is a sibling and not a child, because a
 * button inside a button is invalid HTML that browsers resolve by unnesting, at
 * which point the × stops working for reasons nobody can see.
 *
 * The mark is `iconFor`'s guess at what the row is ABOUT — see `lib/notifications`
 * — tinted by tone. Here it is `aria-hidden`: inside a menu item whose accessible
 * name is already the title and the body, a fourth announced word ("Credits,
 * 500 credits added, your purchase went through…") is noise. The notifications
 * page, where the icon sits in its own cell, labels it properly.
 */
function NotificationRow({
  item,
  onOpen,
  onDismiss,
}: {
  item: NotificationRecord
  onOpen: () => void
  onDismiss: () => void
}) {
  const mark = iconFor(item)
  const details = detailsOf(item).slice(0, PANEL_DETAILS)

  return (
    <li className={`notif-row ${item.readAt ? 'is-read' : 'is-unread'}`}>
      <button type="button" className="notif-row-main" role="menuitem" onClick={onOpen}>
        <span className={`notif-mark tone-${item.tone}`} aria-hidden="true" title={mark.label}>
          <svg viewBox="0 0 20 20" focusable="false">
            <path
              d={mark.path}
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <span className="notif-text">
          <b>{item.title}</b>
          {item.body && <span className="notif-body">{item.body}</span>}
          {/* Spans, where the notifications page uses a `<dl>` for the same
              pairs. Everything here is inside a `<button>`, whose content model
              is phrasing only — a `<dl>` in it is markup the browser silently
              rearranges, and a row that renders correctly today by accident is
              a row that stops rendering on some future engine. */}
          {details.length > 0 && (
            <span className="notif-details">
              {details.map((d) => (
                <span key={`${d.label}:${d.value}`} className="notif-detail">
                  <span className="notif-detail-label">{d.label}</span>
                  <span className="notif-detail-value">{d.value}</span>
                </span>
              ))}
            </span>
          )}
          <span className="notif-when">
            {relativeTime(item.createdAt)}
            {/* The operations feed and a personal notification arrive in the
                same list, and an administrator needs to tell them apart: "a
                member bought credits" and "you were given credits" are
                different facts with similar words. */}
            {item.audience === 'admin' && <em>· system</em>}
          </span>
        </span>
        {!item.readAt && <span className="notif-dot" aria-hidden="true" />}
      </button>
      <button
        type="button"
        className="notif-dismiss"
        title="Remove this notification"
        aria-label={`Remove: ${item.title}`}
        onClick={onDismiss}
      >
        <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
          <path
            d="M6.3 6.3l7.4 7.4M13.7 6.3l-7.4 7.4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </li>
  )
}
